import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

const normalizeToArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === null || value === undefined || value === '') {
        return [];
    }

    return [value];
};

const resolveFilePath = (fileEntry) => {
    if (typeof fileEntry === 'string') return fileEntry;
    if (!fileEntry || typeof fileEntry !== 'object') return '';

    return (
        fileEntry?.file_path ||
        fileEntry?.fileName ||
        fileEntry?.file_name ||
        fileEntry?.path ||
        fileEntry?.url ||
        fileEntry?.file ||
        fileEntry?.image ||
        (typeof fileEntry?.data === 'string' ? fileEntry.data : '') ||
        fileEntry?.data?.file_path ||
        fileEntry?.data?.fileName ||
        fileEntry?.data?.file_name ||
        fileEntry?.data?.path ||
        fileEntry?.data?.url ||
        fileEntry?.data?.file ||
        fileEntry?.data?.image ||
        fileEntry?.originalName ||
        fileEntry?.original_name ||
        ''
    );
};

const normalizeUploadedFiles = (payloadData) => {
    const collectedFiles = [];
    const visited = new Set();
    const queue = normalizeToArray(payloadData);

    while (queue.length) {
        const current = queue.shift();

        if (current === null || current === undefined || current === '') {
            continue;
        }

        if (typeof current === 'string') {
            if (current.trim()) {
                collectedFiles.push(current.trim());
            }
            continue;
        }

        if (typeof current !== 'object') {
            continue;
        }

        if (visited.has(current)) {
            continue;
        }
        visited.add(current);

        const directPath = resolveFilePath(current);
        if (directPath) {
            collectedFiles.push(current);
            continue;
        }

        [
            current?.data,
            current?.files,
            current?.results,
            current?.items,
            current?.rows,
            current?.media,
            current?.attachments,
            current?.uploadedFiles,
            current?.uploaded_files,
        ].forEach((entry) => {
            normalizeToArray(entry).forEach((item) => queue.push(item));
        });

        Object.values(current).forEach((entry) => {
            if (entry && typeof entry === 'object') {
                queue.push(entry);
            }
        });
    }

    return collectedFiles;
};

export const uploadFiles = createAsyncThunk(
    'files/uploadFiles',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await apiService.uploadFiles(formData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to upload file');
        }
    }
);

const initialState = {
    uploadedFiles: [],
    uploadLoading: false,
    error: null,
};

const fileSlice = createSlice({
    name: 'files',
    initialState,
    reducers: {
        clearUploadedFiles: (state) => {
            state.uploadedFiles = [];
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadFiles.pending, (state) => {
                state.uploadLoading = true;
                state.error = null;
            })
            .addCase(uploadFiles.fulfilled, (state, action) => {
                state.uploadLoading = false;
                state.uploadedFiles = normalizeUploadedFiles(action.payload);
                state.error = null;
            })
            .addCase(uploadFiles.rejected, (state, action) => {
                state.uploadLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearUploadedFiles, clearError } = fileSlice.actions;

export default fileSlice.reducer;
